import { Icon, MenuItem, ModuleHeaderSlot, Select, Stack, styled, Typography } from "@tm/components"
import { useLocalization } from "@tm/localization"
import { useOwnListsCompilationsParameters, useWorkTaskId } from "@tm/context-distribution"
import { useMemo } from "react"
import { useHistory, useRouteMatch, generatePath } from "react-router"
import { useOwnListsStore } from "../store"
import type { ListType } from "../component"

enum OwnListTypes {
    FavoriteArticles = "FAVORITE_ARTICLES",
    OwnArticles = "OWN_ARTICLES",
    OwnRepairTimes = "OWN_REPAIR_TIMES",
}

type MenuItems = {
    icon?: string
    key: ListType
    text: string
}

type NavigationProps = {
    listType: ListType
}

const StyledSelect = styled(Select)(() => ({
    height: "32px",
    marginRight: "8px",
    minWidth: "200px",
}))

export function Navigation({ listType }: NavigationProps) {
    const { translateText } = useLocalization()
    const { hasMyArticles, hasMyWorks, hasFavoriteArticles } = useOwnListsCompilationsParameters()
    const history = useHistory()
    const routeMatch = useRouteMatch()
    const workTaskId = useWorkTaskId()

    const menuItems = useMemo(() => {
        const activeMenuItems: MenuItems[] = []

        if (workTaskId) {
            if (hasFavoriteArticles) {
                activeMenuItems.push({
                    text: translateText(13905),
                    key: "favorite-articles",
                    icon: "filled-star",
                })
            }
            if (hasMyArticles) {
                activeMenuItems.push({
                    text: translateText(850),
                    key: "own-articles",
                    icon: "individual-article",
                })
            }
            if (hasMyWorks) {
                activeMenuItems.push({
                    text: translateText(852),
                    key: "own-repair-times",
                    icon: "individual-repairtimes",
                })
            }
        }

        return activeMenuItems
    }, [hasFavoriteArticles, hasMyArticles, hasMyWorks, workTaskId, translateText])
    const { basePath } = useOwnListsStore()
    function handleSelectChange(value: OwnListTypes | string) {
        const selectedItem = menuItems.find((item) => item.key === value)
        if (!selectedItem) {
            return
        }

        const baseUrl = generatePath(basePath, routeMatch.params)

        history.push(`${baseUrl}${selectedItem.key}`)
    }

    const activeMenuItem = useMemo(() => menuItems.find((x) => x.key === listType), [listType, menuItems])

    return (
        <>
            <Typography variant="h1" pr={2} pb={1}>
                {translateText(13944)}
            </Typography>

            <ModuleHeaderSlot hideDivider>
                <StyledSelect
                    onChange={(e) => handleSelectChange(e.target.value as OwnListTypes)}
                    variant="outlined"
                    defaultValue={activeMenuItem?.key || menuItems[0]?.key}
                    renderValue={(val) => {
                        const selectedItem = menuItems.find((item) => item.key === val)
                        return selectedItem ? (
                            <Stack direction="row" gap={1} alignItems="center">
                                <Icon name={selectedItem.icon} />
                                {selectedItem.text}
                            </Stack>
                        ) : null
                    }}
                    disableUnderline
                    iconOnTheRight
                >
                    {menuItems.map((item) => (
                        <MenuItem key={item.key} value={item.key}>
                            <Stack direction="row" gap={1} alignItems="center">
                                <Icon name={item.icon} />
                                {item.text}
                            </Stack>
                        </MenuItem>
                    ))}
                </StyledSelect>
            </ModuleHeaderSlot>
        </>
    )
}
